import { useEffect, useRef, useState } from 'react';
import { VEIN_SEVER_SLASH_DURATION_MS } from '../config/veinSeverEffect';

/** Survives Strict Mode remounts so first hydration does not consume refs twice incorrectly */
const veinSeverProcessedKeysByGame = new Map();
const veinSeverHydratedGames = new Set();

function mapVeinSeverToViewerSlot(fx, viewerUserId) {
  if (!fx || fx.zone == null || fx.slotIndex == null || fx.targetPlayerId == null) {
    return null;
  }
  const isOwnerViewer = String(fx.targetPlayerId) === String(viewerUserId);
  const prefix = isOwnerViewer ? 'your' : 'opponent';
  return {
    zone: `${prefix}_${fx.zone}`,
    index: fx.slotIndex,
  };
}

function isVeinSeverStrategem(s) {
  if (!s || s.instanceId == null) return false;
  if (Number(s.id) !== 55) return false;
  const t = (s.type && String(s.type)) || '';
  if (!t) return true;
  return /strategem/i.test(t);
}

/**
 * Strategem 55 (Vein Sever): slash FX when `veinSeverFx` is on completed order.
 */
export function useVeinSeverSlash(gameState, viewerUserId) {
  const [activeVeinSeverSlashes, setActiveVeinSeverSlashes] = useState([]);
  const gameIdRef = useRef(null);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null) return;

    const gid = gameState.gameId != null ? String(gameState.gameId) : '_';

    if (gameIdRef.current !== gid) {
      gameIdRef.current = gid;
      setActiveVeinSeverSlashes([]);
    }

    if (!veinSeverProcessedKeysByGame.has(gid)) {
      veinSeverProcessedKeysByGame.set(gid, new Set());
    }
    const processed = veinSeverProcessedKeysByGame.get(gid);

    if (!veinSeverHydratedGames.has(gid)) {
      veinSeverHydratedGames.add(gid);
      Object.keys(gameState.state).forEach((pid) => {
        gameState.state[pid]?.completedOrders?.forEach((st) => {
          if (isVeinSeverStrategem(st)) {
            processed.add(`vs55-${pid}-${st.instanceId}`);
          }
        });
      });
      return;
    }

    Object.keys(gameState.state).forEach((casterId) => {
      const player = gameState.state[casterId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach((strategem) => {
        if (!isVeinSeverStrategem(strategem)) return;
        const fx = strategem.veinSeverFx;
        if (!fx) return;

        const dedupeKey = `vs55-${casterId}-${strategem.instanceId}`;
        if (processed.has(dedupeKey)) return;
        processed.add(dedupeKey);

        const viewerSlot = mapVeinSeverToViewerSlot(fx, viewerUserId);
        if (!viewerSlot) return;

        const animKey = `vs55-${strategem.instanceId}-${Date.now()}`;
        setActiveVeinSeverSlashes((prev) => [
          ...prev,
          { key: animKey, viewerSlot, animationKey: animKey },
        ]);

        window.setTimeout(() => {
          setActiveVeinSeverSlashes((prev) => prev.filter((p) => p.key !== animKey));
        }, VEIN_SEVER_SLASH_DURATION_MS + 100);
      });
    });

    const setForGame = veinSeverProcessedKeysByGame.get(gid);
    if (setForGame && setForGame.size > 120) {
      const keys = Array.from(setForGame);
      keys.slice(0, keys.length - 80).forEach((k) => setForGame.delete(k));
    }
  }, [gameState, gameState?.stateVersion, viewerUserId]);

  return { activeVeinSeverSlashes };
}

export default useVeinSeverSlash;
